import {
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { HelpCircle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "components/ds";
import { isQueryNoteValid } from "../reviewLogic";

interface QueryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Display name of the account the query is about (for the subtitle). */
  accountName?: string | null;
  /**
   * Subtitle under the title. Defaults to "About {accountName}" when
   * `accountName` is set — pass an explicit value for the review-level case
   * (e.g. "About this review"), where there is no account.
   */
  subtitle?: string;
  /** Textarea placeholder. Defaults to the account-scoped prompt. */
  placeholder?: string;
  isSubmitting?: boolean;
  /** The review takes no more changes (its cycle ended): confirm is off. */
  isLocked?: boolean;
  onSubmit: (note: string) => void;
}

/**
 * QueryModal (PLO-4803 P4) — raise a query to the campaign admin. The note is
 * MANDATORY: it IS the question, so submit is blocked until it's non-blank. Used
 * both account-scoped (posts `review: "query"`) and review-scoped (posts a
 * general query), with the caller wiring the mutation + copy.
 */
const QueryModal = ({
  isOpen,
  onClose,
  accountName,
  subtitle,
  placeholder,
  isSubmitting,
  isLocked = false,
  onSubmit,
}: QueryModalProps) => {
  const [note, setNote] = useState("");

  useEffect(() => {
    if (isOpen) setNote("");
  }, [isOpen]);

  const valid = isQueryNoteValid(note);
  const subtitleText = subtitle ?? (accountName ? `About ${accountName}` : null);

  const handleSubmit = () => {
    if (!valid || isLocked) return;
    onSubmit(note.trim());
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="rgba(0, 0, 0, 0.3)" backdropFilter="blur(3px)" />
      <ModalContent borderRadius="12px" boxShadow="lg">
        <ModalHeader
          fontSize="md"
          fontWeight="500"
          color="text.body.primary"
          pb={2}
          borderBottom="1px solid"
          borderColor="border.default"
        >
          <HStack spacing="sm" align="center">
            <Icon as={HelpCircle} boxSize="16px" color="text.body.secondary" />
            <VStack align="flex-start" spacing={0} minW={0}>
              <Text>Raise a query</Text>
              {subtitleText && (
                <Text
                  fontSize="xs"
                  fontWeight="400"
                  color="text.body.tertiary"
                  noOfLines={1}
                >
                  {subtitleText}
                </Text>
              )}
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton size="sm" top="12px" right="12px" />

        <ModalBody py={4} px={4}>
          <FormControl isRequired isInvalid={note.length > 0 && !valid}>
            <FormLabel fontSize="sm" fontWeight="500" mb={1}>
              Your question
            </FormLabel>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                placeholder ?? "Ask the campaign admin about this account…"
              }
              fontSize="sm"
              borderRadius="8px"
              rows={4}
              resize="vertical"
              autoFocus
            />
            <FormErrorMessage fontSize="xs">
              A question is required to raise a query.
            </FormErrorMessage>
          </FormControl>
        </ModalBody>

        <Divider borderColor="border.default" />

        <ModalFooter pt={3} pb={3}>
          <HStack spacing={2}>
            <Button
              variant="subtle"
              tone="neutral"
              size="md"
              onClick={onClose}
              isDisabled={isSubmitting}
              leadingIcon={<Icon as={X} boxSize="14px" />}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              tone="primary"
              size="md"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={!valid || isLocked}
            >
              Send query
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default QueryModal;
